import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Modal from 'components/Modal/Modal';
import ProjectThumbnailCard from 'components/TextProjects/ProjectThumbnailCard/ProjectThumbnailCard';
import CropWorkspace from 'components/ImageCrop/CropWorkspace';
import {
  attachCropDialogChrome,
  PREVIEW_THROTTLE_MS,
  readFileAsDataUrl,
} from 'components/ImageCrop/cropModalUtils';
import { getAutomaticCoverCrop } from 'helpers/projectCoverAutoCrop';
import styles from './AdjustThumbnailModal.module.scss';

const TITLE_ID = 'AdjustThumbnailModal-title';
const DESC_ID = 'AdjustThumbnailModal-desc';

/**
 * Shared “Adjust thumbnail” modal for project image, video-override, and audio cover flows.
 * Crop frame is locked to the card aspect ratio — free-ratio cropping is not available.
 */
const AdjustThumbnailModal = ({
  show,
  aspectRatio,
  initialFile,
  initialSrc,
  initialCrop,
  projectTitle,
  projectDescription,
  firstRole,
  onCancel,
  onSave,
}) => {
  const dialogRef = useRef(null);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const appliedInitialCropRef = useRef(false);
  const previewTimerRef = useRef(null);
  const suppressSourceChangeRef = useRef(false);
  /** True after the user picks a replacement file in this modal session. */
  const userSelectedFileRef = useRef(false);
  /** Canvas + crop box for the automatic cover crop (zoom/pan baseline). */
  const automaticSnapshotRef = useRef(null);

  const [src, setSrc] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [pendingSource, setPendingSource] = useState('custom');
  const [ready, setReady] = useState(false);

  const isSquare = aspectRatio === 1;
  const displayTitle = projectTitle || 'Untitled project';

  const getCropper = useCallback(() => {
    return cropperRef.current && cropperRef.current.cropper
      ? cropperRef.current.cropper
      : null;
  }, []);

  const updatePreview = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const canvas = cropper.getCroppedCanvas({
        width: isSquare ? 240 : 320,
        height: isSquare ? 240 : 180,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'low',
      });
      if (canvas) {
        setPreviewUrl(canvas.toDataURL('image/jpeg', 0.7));
      }
    } catch (err) {
      // Cross-origin taint — keep last preview if any.
    }

    try {
      const imageData = cropper.getImageData();
      if (imageData && imageData.naturalWidth) {
        const ratio = imageData.width / imageData.naturalWidth;
        setZoom(ratio);
      }
    } catch (err) {
      // ignore
    }
  }, [getCropper, isSquare]);

  const schedulePreview = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      previewTimerRef.current = null;
      updatePreview();
    }, PREVIEW_THROTTLE_MS);
  }, [updatePreview]);

  const syncZoomBounds = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const imageData = cropper.getImageData();
      const canvasData = cropper.getCanvasData();
      if (!imageData || !imageData.naturalWidth || !canvasData) {
        return;
      }

      // Minimum zoom keeps the image covering the crop box (no letterboxing).
      const coverZoom = Math.max(
        canvasData.width / imageData.naturalWidth,
        canvasData.height / imageData.naturalHeight
      );
      const nextMin = Number.isFinite(coverZoom) ? coverZoom * 0.98 : 0.1;
      const nextMax = Math.max(nextMin * 4, 3);

      setMinZoom(nextMin);
      setMaxZoom(nextMax);
      setZoom(imageData.width / imageData.naturalWidth);
    } catch (err) {
      // ignore
    }
  }, [getCropper]);

  const captureAutomaticSnapshot = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      automaticSnapshotRef.current = {
        data: cropper.getData(),
        canvasData: cropper.getCanvasData(),
        cropBoxData: cropper.getCropBoxData(),
      };
    } catch (err) {
      automaticSnapshotRef.current = null;
    }
  }, [getCropper]);

  const applyAutomaticCrop = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    suppressSourceChangeRef.current = true;

    const snapshot = automaticSnapshotRef.current;
    if (snapshot && snapshot.canvasData && snapshot.cropBoxData) {
      // Restore exact automatic zoom/pan + crop frame (not just crop coords).
      cropper.setCanvasData(snapshot.canvasData);
      cropper.setCropBoxData(snapshot.cropBoxData);
    } else {
      // Undo any user zoom/pan, then apply the centre-cover automatic crop.
      cropper.reset();

      const imageData = cropper.getImageData();
      const auto = getAutomaticCoverCrop(
        imageData.naturalWidth,
        imageData.naturalHeight,
        aspectRatio
      );

      if (auto) {
        cropper.setData({
          x: auto.x,
          y: auto.y,
          width: auto.width,
          height: auto.height,
        });
      }

      captureAutomaticSnapshot();
    }

    setPendingSource('auto');
    syncZoomBounds();
    updatePreview();

    // cropperjs emits crop events for setData/reset — don't flip source to custom.
    window.setTimeout(() => {
      suppressSourceChangeRef.current = false;
      syncZoomBounds();
      updatePreview();
    }, 0);
  }, [
    aspectRatio,
    captureAutomaticSnapshot,
    getCropper,
    syncZoomBounds,
    updatePreview,
  ]);

  const loadSource = useCallback(async (file, url) => {
    appliedInitialCropRef.current = false;
    automaticSnapshotRef.current = null;
    setReady(false);
    setPreviewUrl(null);

    if (file instanceof File || file instanceof Blob) {
      const dataUrl = await readFileAsDataUrl(file);
      setSrc(dataUrl);
      setOriginalImage(file);
      return;
    }

    if (url) {
      setSrc(url);
      setOriginalImage(url);
      return;
    }

    setSrc(null);
    setOriginalImage(null);
  }, []);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    userSelectedFileRef.current = false;
    setPendingSource(initialCrop ? 'custom' : 'auto');
    loadSource(initialFile, initialSrc);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [show, initialFile, initialSrc, initialCrop, loadSource]);

  // Focus trap + restore focus + arrow nudge
  useEffect(() => {
    if (!show) {
      return undefined;
    }

    return attachCropDialogChrome({
      dialogRef,
      getCropper,
      src,
      onNudge: () => {
        setPendingSource('custom');
        schedulePreview();
      },
      previousFocusRef,
    });
  }, [show, getCropper, schedulePreview, src]);

  const handleReady = () => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    setReady(true);

    // Always establish the automatic baseline first so Reset can restore zoom+crop.
    suppressSourceChangeRef.current = true;
    cropper.reset();

    const imageData = cropper.getImageData();
    const auto = getAutomaticCoverCrop(
      imageData.naturalWidth,
      imageData.naturalHeight,
      aspectRatio
    );

    if (auto) {
      cropper.setData({
        x: auto.x,
        y: auto.y,
        width: auto.width,
        height: auto.height,
      });
    }
    captureAutomaticSnapshot();

    if (initialCrop && !appliedInitialCropRef.current) {
      const { x, y, width, height } = initialCrop;
      if (
        typeof x === 'number' &&
        typeof y === 'number' &&
        typeof width === 'number' &&
        typeof height === 'number'
      ) {
        appliedInitialCropRef.current = true;
        cropper.setData({
          x: x < 0 ? 0 : x,
          y: y < 0 ? 0 : y,
          width,
          height,
        });
        setPendingSource('custom');
      } else {
        setPendingSource('auto');
      }
    } else {
      setPendingSource('auto');
    }

    syncZoomBounds();
    updatePreview();

    window.setTimeout(() => {
      suppressSourceChangeRef.current = false;
      syncZoomBounds();
      updatePreview();
    }, 0);
  };

  const handleCrop = () => {
    if (!suppressSourceChangeRef.current) {
      setPendingSource('custom');
    }
    schedulePreview();
  };

  const handleZoomSlider = (event) => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    const next = Number(event.target.value);
    if (!Number.isFinite(next)) {
      return;
    }

    cropper.zoomTo(next);
    setZoom(next);
    setPendingSource('custom');
    schedulePreview();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    userSelectedFileRef.current = true;
    setPendingSource('auto');
    await loadSource(file, null);
  };

  const handleReset = () => {
    applyAutomaticCrop();
  };

  const handleSave = () => {
    // Null only clears the project's own auto media source — not a newly
    // selected file that still happens to sit on the automatic crop.
    if (pendingSource === 'auto' && !userSelectedFileRef.current) {
      onSave(null);
      return;
    }

    const cropper = getCropper();
    if (!cropper || !src) {
      return;
    }

    const data = cropper.getData(true);
    let croppedImage = null;
    try {
      const canvas = cropper.getCroppedCanvas({
        width: isSquare ? 240 : 320,
        height: isSquare ? 240 : 180,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      croppedImage = canvas
        ? canvas.toDataURL('image/jpeg', 0.85)
        : null;
    } catch (err) {
      croppedImage = null;
    }

    onSave({
      x: data.x < 0 ? 0 : data.x,
      y: data.y < 0 ? 0 : data.y,
      width: data.width,
      height: data.height,
      image: originalImage || initialSrc || null,
      croppedImage,
    });
  };

  const canSave = Boolean(src) && ready;

  return (
    <Modal
      show={show}
      onExit={onCancel}
      backgroundExit={false}
      closeButton={false}
      animation="scale"
      modalClassName={styles.modal}
      modalStyle={{
        width: 'calc(100% - 24px)',
        maxWidth: 880,
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESC_ID}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <h2 className={styles.title} id={TITLE_ID}>
            Adjust thumbnail
          </h2>
          <p className={styles.subhead} id={DESC_ID}>
            Choose what shows on portfolio cards and in client searches.
          </p>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className={styles.body}>
          <div className={styles.workspaceColumn}>
            <CropWorkspace
              src={src}
              cropperRef={cropperRef}
              aspectRatio={aspectRatio}
              maskShape="rect"
              cropBoxResizable
              onReady={handleReady}
              onCrop={handleCrop}
              onZoom={schedulePreview}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={maxZoom}
              onZoomChange={handleZoomSlider}
              showZoom
              emptyTitle="Upload an image to crop your thumbnail."
              emptyHint=""
              onEmptyClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              hint="Drag to reposition · pinch or scroll to zoom"
            />
          </div>

          <div className={styles.previewColumn}>
            <p className={styles.previewEyebrow}>Preview</p>

            <ProjectThumbnailCard
              imageUrl={previewUrl}
              title={displayTitle}
              description={projectDescription}
              firstRole={firstRole}
              cropSource={pendingSource}
              square={isSquare}
              width={isSquare ? 240 : 280}
            />

            <div className={styles.searchPreview}>
              <div
                className={`${styles.searchThumb}${
                  isSquare ? ` ${styles.square}` : ''
                }`}
              >
                {previewUrl ? <img src={previewUrl} alt="" /> : null}
              </div>
              <div className={styles.searchMeta}>
                <p className={styles.searchTitle}>{displayTitle}</p>
                <p className={styles.searchCaption}>Search result size</p>
              </div>
            </div>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            <button
              type="button"
              className={styles.resetLink}
              onClick={handleReset}
              disabled={!src || !ready}
            >
              Reset to automatic
            </button>
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={!canSave}
            >
              Save thumbnail
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

AdjustThumbnailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  aspectRatio: PropTypes.number.isRequired,
  initialFile: PropTypes.object,
  initialSrc: PropTypes.string,
  initialCrop: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  projectTitle: PropTypes.string,
  projectDescription: PropTypes.string,
  firstRole: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  /** Called with crop details for a custom crop, or `null` to persist automatic. */
  onSave: PropTypes.func.isRequired,
};

AdjustThumbnailModal.defaultProps = {
  initialFile: null,
  initialSrc: null,
  initialCrop: null,
  projectTitle: '',
  projectDescription: '',
  firstRole: '',
};

export default AdjustThumbnailModal;
